import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom"; // ✅ added useNavigate
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import BusinessIcon from "@mui/icons-material/Business";
import TagIcon from "@mui/icons-material/Tag";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import NotesIcon from "@mui/icons-material/Notes";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import { useAppServices } from "hook/services";
import toast from "react-hot-toast";
import dayjs from "dayjs";


const Badge = ({ label, bg, color }) => (
  <span style={{
    background: bg, color, padding: "3px 12px",
    borderRadius: 20, fontSize: 11, fontWeight: 600, display: "inline-block",
  }}>
    {label}
  </span>
);

const SectionLabel = ({ children, mt = 4 }) => (
  <p style={{
    fontSize: 12, fontWeight: 600, color: "#888780",
    marginTop: mt, marginBottom: 4,
    textTransform: "uppercase", letterSpacing: "0.05em",
  }}>
    {children}
  </p>
);

const InfoRow = ({ icon: Icon, label, value, link }) => (
  <div style={{
    display: "flex", alignItems: "flex-start", gap: 10,
    padding: "10px 0", borderBottom: "0.5px solid #f0ece8",
  }}>
    <Icon sx={{ fontSize: 16, color: "#888780", mt: "2px", flexShrink: 0 }} />
    <span style={{ width: 130, fontSize: 13, color: "#888780", flexShrink: 0 }}>{label}</span>
    {link ? (
      <a
        href={link.startsWith("http") ? link : `https://${link}`}
        target="_blank" rel="noreferrer"
        style={{ fontSize: 13, fontWeight: 500, color: "#534AB7", flex: 1, wordBreak: "break-all" }}
      >
        {value}
      </a>
    ) : (
      <span style={{ fontSize: 13, fontWeight: 500, color: "#2C2C2A", flex: 1 }}>{value || "—"}</span>
    )}
  </div>
);

const ChangeCompare = ({ label, from, to, changed }) => (
  <div style={{
    background: changed ? "#FAEEDA" : "#F9F8F6",
    border: `0.5px solid ${changed ? "#FAC775" : "#E8E5E0"}`,
    borderRadius: 10, padding: "12px 14px",
  }}>
    <p style={{ fontSize: 11, color: "#888780", marginBottom: 6, fontWeight: 500 }}>{label}</p>
    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
      <span style={{
        fontSize: 13, color: "#501313",
        textDecoration: changed ? "line-through" : "none",
        background: changed ? "#FCEBEB" : "transparent",
        padding: changed ? "2px 8px" : 0, borderRadius: 6,
      }}>
        {from || "—"}
      </span>
      {changed && (
        <>
          <span style={{ color: "#B4B2A9", fontSize: 13 }}>→</span>
          <span style={{
            fontSize: 13, fontWeight: 600, color: "#27500A",
            background: "#EAF3DE", padding: "2px 8px", borderRadius: 6,
          }}>
            {to || "—"}
          </span>
        </>
      )}
    </div>
  </div>
);


export default function ChangeRequestDetailPage() {
  const { bookingNo } = useParams();   
  const navigate = useNavigate();     
  const AppService = useAppServices();

  const [booking, setBooking] = useState(null);     
  const [bookingLoading, setBookingLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [actionDone, setActionDone] = useState(null);
  const [req, setReq] = useState(null);
  const [reqLoading, setReqLoading] = useState(false);  
  const [hostUser, setHostUser] = useState(null);
  const [creativeUser, setCreativeUser] = useState(null);

  useEffect(() => {
    if (!bookingNo) return;
    const fetchBooking = async () => {
      setBookingLoading(true);
      try {
        const query = `bookingNo=${encodeURIComponent(bookingNo)}`;
        const { response } = await AppService.booking.get({ query });
        const found = (response?.data || []).find((b) => b.bookingNo === bookingNo);
        setBooking(found || null);
      } catch (err) {
        console.error("Failed to fetch booking:", err);
        setBooking(null);
      } finally {
        setBookingLoading(false);
      }
    };
    fetchBooking();
  }, [bookingNo]);

  // ── fetch change request ───────────────────────────────────────────────────
  useEffect(() => {
    if (!booking) return;
    const fetchReq = async () => {                      // ✅ FIX 7: renamed inner fn to avoid shadowing
      setReqLoading(true);
      try {
        const { response } = await AppService.bookingChangeRequest.getByBooking({
          query: `bookingNo=${bookingNo}`,
        });
        if (response?.success && response.data?.length > 0) {
          const pending = response.data.find(r => r.status === "pending") || response.data[0];
          setReq(pending);
        }
      } catch (err) {
        console.error("Failed to fetch change request:", err);
      } finally {
        setReqLoading(false);
      }
    };
    fetchReq();
  }, [booking?.bookingNo]);

  // ── fetch creative user ────────────────────────────────────────────────────
  useEffect(() => {
    const shoot = booking?.shootDetail || {};
    if (!shoot?.bookedBy) return;
    const creativeId = typeof shoot.bookedBy === "object" ? shoot.bookedBy._id : shoot.bookedBy;
    if (!creativeId) return;
    const fetchCreative = async () => {
      try {
        const { response } = await AppService.user.get({ query: `_id=${creativeId}` });
        if (response?.success && response.data) setCreativeUser(response.data);
      } catch (err) { console.error("Failed to fetch creative user:", err); }
    };
    fetchCreative();
  }, [booking?.shootDetail?.bookedBy]);

  // ── fetch host user ────────────────────────────────────────────────────────
  useEffect(() => {
    const listing = booking?.shootDetail?.listing || {};
    if (!listing?.user_id) return;
    const hostId = typeof listing.user_id === "object" ? listing.user_id._id : listing.user_id;
    if (!hostId) return;
    const fetchHost = async () => {
      try {
        const { response } = await AppService.user.get({ query: `_id=${hostId}` });
        if (response?.success && response.data) setHostUser(response.data);
      } catch (err) { console.error("Failed to fetch host user:", err); }
    };
    fetchHost();
  }, [booking?.shootDetail?.listing?.user_id]);

  // ── loading / not found states (after all hooks) ───────────────────────────
  if (bookingLoading) {
    return (
      <div style={{ padding: 40, textAlign: "center", color: "#888780", fontSize: 14 }}>
        Loading booking...
      </div>
    );
  }

  if (!booking) {                                        // ✅ FIX 8: safe early return AFTER hooks
    return (
      <div style={{ padding: 40, textAlign: "center" }}>
        <p style={{ color: "#A32D2D", fontSize: 14 }}>No booking found for <strong>{bookingNo}</strong></p>
        <button
          onClick={() => navigate("/Bookings")}
          style={{ marginTop: 16, padding: "8px 20px", borderRadius: 20, background: "#534AB7", color: "#fff", border: "none", cursor: "pointer", fontSize: 13 }}
        >
          Back to Bookings
        </button>
      </div>
    );
  }

  // ── derived values (booking is guaranteed non-null here) ───────────────────
  const shoot = booking?.shootDetail || {};
  const listing = shoot.listing || {};

  const currentDate = shoot.shootDate ? dayjs(shoot.shootDate).format("DD/MM/YYYY") : "—";
  const requestedDate = req?.requestedDate ? dayjs(req.requestedDate).format("DD/MM/YYYY") : null;
  const currentStart = shoot.startTime || "—";
  const currentEnd = shoot.endTime || "—";
  const reqStart = req?.requestedStartTime || null;
  const reqEnd = req?.requestedEndTime || null;

  const dateChanged = requestedDate && currentDate !== requestedDate;
  const startChanged = reqStart && currentStart !== reqStart;
  const endChanged = reqEnd && currentEnd !== reqEnd;
  const anyChanged = dateChanged || startChanged || endChanged;

  const requestedAt = req?.createdAt ? dayjs(req.createdAt).format("DD MMM YYYY [at] HH:mm") : "—";
  const requestedByLabel = req?.requestedByRole
    ? req.requestedByRole.charAt(0).toUpperCase() + req.requestedByRole.slice(1) : "—";

  const creativeName =
    creativeUser
      ? `${creativeUser.firstName || creativeUser.name || ""} ${creativeUser.lastName || ""}`.trim()
      : `${booking?.firstName || ""} ${booking?.lastName || ""}`.trim() || "—";

  const hostName = hostUser
    ? `${hostUser.displayName || hostUser.firstName || ""}`.trim()
    : typeof listing.user_id === "object"
      ? `${listing.user_id.firstName || listing.user_id.name || ""} ${listing.user_id.lastName || ""}`.trim()
      : "—";

  // ── handlers ──────────────────────────────────────────────────────────────
  const handleApprove = async () => {
    if (!req?._id) return toast.error("Change request not loaded yet.");
    if (!window.confirm("Approve this change request? The booking will be updated immediately.")) return;
    setLoading(true);
    try {
      const hostId = typeof listing?.user_id === "object" ? listing.user_id._id : listing?.user_id;
      const { response } = await AppService.bookingChangeRequest.accept({ payload: { id: req._id, hostId } });
      if (response?.success) {
        toast.success("Change request approved! Booking updated.");
        setActionDone("approved");
        navigate("/Bookings");

      } else {
        toast.error(response?.message || "Approval failed.");
      }
    } catch { toast.error("Something went wrong while approving."); }
    finally { setLoading(false); }
  };

  const handleReject = async () => {
    if (!req?._id) return toast.error("Change request not loaded yet.");
    if (!window.confirm("Reject this change request?")) return;   // ✅ FIX 9: added confirm
    setLoading(true);
    try {
      const { response } = await AppService.bookingChangeRequest.reject({ payload: { id: req._id } });
      if (response?.success) {
        toast.success("Change request rejected. Booking unchanged.");
        setActionDone("rejected");
        navigate("/Bookings");
      } else {
        toast.error(response?.message || "Rejection failed.");
      }
    } catch { toast.error("Something went wrong while rejecting."); }
    finally { setLoading(false); }
  };

  // if (actionDone) {
  //   const approved = actionDone === "approved";
  //   return (
  //     <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "60px 24px", gap: 16, textAlign: "center" }}>
  //       <div style={{ width: 64, height: 64, borderRadius: "50%", background: approved ? "#EAF3DE" : "#FCEBEB", display: "flex", alignItems: "center", justifyContent: "center" }}>
  //         {approved ? <CheckCircleIcon sx={{ fontSize: 32, color: "#3B6D11" }} /> : <CancelIcon sx={{ fontSize: 32, color: "#A32D2D" }} />}
  //       </div>
  //       <p style={{ fontSize: 18, fontWeight: 500 }}>Request {approved ? "approved" : "rejected"}</p>
  //       <p style={{ fontSize: 13, color: "#888780", maxWidth: 320 }}>
  //         {approved ? "The booking has been updated and both parties have been notified." : "The booking remains unchanged. Both parties have been notified."}
  //       </p>
  //       <button
  //         onClick={() => navigate("/Bookings")}       
  //         style={{ marginTop: 8, padding: "9px 28px", borderRadius: 20, background: "#534AB7", color: "#fff", border: "none", fontSize: 13, fontWeight: 600, cursor: "pointer" }}
  //       >
  //         Back to bookings
  //       </button>
  //     </div>
  //   );
  // }

  if (reqLoading) {
    return <div style={{ padding: 40, textAlign: "center", color: "#888780", fontSize: 14 }}>Loading change request...</div>;
  }

  // ── main view ──────────────────────────────────────────────────────────────
  return (
    <div style={{ padding: "0 0 32px", maxWidth: 860, margin: "0 auto" }}>

      {/* top bar */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "18px 0 20px", borderBottom: "0.5px solid #EAE5DF", flexWrap: "wrap", gap: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <button
            onClick={() => navigate("/bookings")}         // ✅ FIX 11: navigate instead of onClose
            style={{ display: "flex", alignItems: "center", gap: 6, background: "transparent", border: "0.5px solid #D3D1C7", padding: "6px 14px", borderRadius: 20, cursor: "pointer", fontSize: 12, color: "#5F5E5A" }}
          >
            <ArrowBackIcon sx={{ fontSize: 14 }} /> Back
          </button>
          <div>
            <p style={{ fontSize: 15, fontWeight: 500 }}>Change request</p>
            <p style={{ fontSize: 12, color: "#888780", marginTop: 1 }}>Booking #{booking.bookingNo} · submitted {requestedAt}</p>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <Badge label="Pending review" bg="#FAEEDA" color="#633806" />
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginTop: 20 }}>
        <div style={{ background: "#fff", border: "0.5px solid #EAE5DF", borderRadius: 12, padding: "16px 18px" }}>
          <SectionLabel mt={0}>Booking info</SectionLabel>
          <InfoRow icon={TagIcon} label="Booking no" value={booking.bookingNo} />
          <InfoRow icon={BusinessIcon} label="Company" value={booking.company} />
          <InfoRow icon={LocationOnIcon} label="Listing" value={listing.title} />
          <InfoRow icon={CalendarTodayIcon} label="Current date" value={currentDate} />
          <InfoRow icon={AccessTimeIcon} label="Current time" value={`${currentStart} – ${currentEnd}`} />
        </div>

        <div style={{ background: "#fff", border: "0.5px solid #EAE5DF", borderRadius: 12, padding: "16px 18px" }}>
          <SectionLabel mt={0}>Requested changes</SectionLabel>
          {!req ? (
            <p style={{ fontSize: 13, color: "#888780" }}>No change request found.</p>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <ChangeCompare label="Date" from={currentDate} to={requestedDate} changed={!!dateChanged} />
              <ChangeCompare label="Start time" from={currentStart} to={reqStart} changed={!!startChanged} />
              <ChangeCompare label="End time" from={currentEnd} to={reqEnd} changed={!!endChanged} />
              {!anyChanged && <p style={{ fontSize: 12, color: "#888780", marginTop: 4 }}>No date/time changes detected.</p>}
            </div>
          )}
          <SectionLabel>Reason provided</SectionLabel>
          <div style={{ background: "#F9F8F6", border: "0.5px solid #EAE5DF", borderRadius: 8, padding: "10px 12px", display: "flex", gap: 8, alignItems: "flex-start" }}>
            <NotesIcon sx={{ fontSize: 16, color: "#888780", mt: "2px", flexShrink: 0 }} />
            <p style={{ fontSize: 13, color: "#2C2C2A", lineHeight: 1.6, margin: 0 }}>{req?.reason || "No reason provided."}</p>
          </div>
          <p style={{ fontSize: 11, color: "#B4B2A9", marginTop: 8 }}>
            Requested by: <strong style={{ color: "#5F5E5A" }}>{requestedByLabel}</strong>
          </p>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginTop: 16 }}>
        <div style={{ background: "#fff", border: "0.5px solid #EAE5DF", borderRadius: 12, padding: "16px 18px" }}>
          <div style={{ marginBottom: 12 }}>
            <p style={{ fontSize: 14, color: "black", margin: 0, fontWeight: 600 }}>Creative Detail</p>
            <p style={{ fontSize: 13, fontWeight: 600, color: "#2C2C2A", margin: "4px 0 0" }}>{creativeName}</p>
          </div>
          <InfoRow icon={EmailIcon} label="Email" value={creativeUser?.email || shoot?.bookedBy?.email || booking?.email || "—"} />
          <InfoRow icon={PhoneIcon} label="Mobile" value={creativeUser?.mobile || creativeUser?.phone || booking?.mobile || "—"} />
        </div>

        <div style={{ background: "#fff", border: "0.5px solid #EAE5DF", borderRadius: 12, padding: "16px 18px" }}>
          <div style={{ marginBottom: 12 }}>
            <p style={{ fontSize: 14, color: "black", margin: 0, fontWeight: 600 }}>Host Detail</p>
            <p style={{ fontSize: 13, fontWeight: 600, color: "#2C2C2A", margin: "4px 0 0" }}>{hostName}</p>
          </div>
          <InfoRow icon={EmailIcon} label="Email" value={hostUser?.email || listing?.user_id?.email || "—"} />
          <InfoRow icon={PhoneIcon} label="Mobile" value={hostUser?.mobile || hostUser?.phone || "—"} />
        </div>
      </div>

      {/* action footer */}
      <div style={{ marginTop: 24, background: "#fff", border: "0.5px solid #EAE5DF", borderRadius: 12, padding: "16px 20px", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
        <div>
          <p style={{ fontSize: 13, fontWeight: 500 }}>Ready to review?</p>
          <p style={{ fontSize: 12, color: "#888780", marginTop: 2 }}>
            Approving will update the booking. Rejecting keeps the original.
          </p>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button
            disabled={loading || !req}
            onClick={handleReject}
            style={{ display: "flex", alignItems: "center", gap: 6, padding: "9px 22px", borderRadius: 20, background: loading || !req ? "#D3D1C7" : "#FCEBEB", color: loading || !req ? "#888780" : "#A32D2D", border: `0.5px solid ${loading || !req ? "#D3D1C7" : "#F09595"}`, fontSize: 13, fontWeight: 600, cursor: loading || !req ? "not-allowed" : "pointer" }}
          >
            <CancelIcon sx={{ fontSize: 16 }} /> Reject
          </button>
          <button
            disabled={loading || !req}
            onClick={handleApprove}
            style={{ display: "flex", alignItems: "center", gap: 6, padding: "9px 22px", borderRadius: 20, background: loading || !req ? "#D3D1C7" : "#534AB7", color: "#fff", border: "none", fontSize: 13, fontWeight: 600, cursor: loading || !req ? "not-allowed" : "pointer" }}
          >
            <CheckCircleIcon sx={{ fontSize: 16 }} />
            {loading ? "Processing…" : "Approve"}
          </button>
        </div>
      </div>

    </div>
  );
}